import React, { useEffect, useState, useMemo } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Box,
  Typography,
  Chip,
  Slider,
  Divider,
  Tooltip
} from "@mui/material";

import LockRoundedIcon from "@mui/icons-material/LockRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";

import { getProject, updateProject, getEmployeeCapacity } from "../../services/projectService";
import AppButton from "../../ui/AppButton";
import AppDatePicker from "../../ui/AppDatePicker";

// local-timezone YYYY-MM-DD (toISOString shifts dates in UTC+ zones).
function localStr(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Returns the Monday of the week containing `date` as a "YYYY-MM-DD" string.
function weekStartKey(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return localStr(d);
}

function buildWeeks(startDate, endDate) {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const weeks = [];
  const monday = new Date(start);
  const day = monday.getDay();
  monday.setDate(monday.getDate() + (day === 0 ? -6 : 1 - day));

  while (monday <= end) {
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    weeks.push({ key: localStr(monday), start: new Date(monday), end: sunday });
    monday.setDate(monday.getDate() + 7);
  }
  return weeks;
}

function fmtDate(date) {
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function capColor(v) {
  if (v >= 100) return "#ef4444";
  if (v >= 75) return "#f97316";
  if (v >= 50) return "#eab308";
  return "#22c55e";
}

function EditCapacityDialog({ open, employee, project, onClose, onSaved }) {
  const [globalCapacity, setGlobalCapacity] = useState(0);
  const [weeklyCapacities, setWeeklyCapacities] = useState({});
  const [availableCapacity, setAvailableCapacity] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const weeks = useMemo(
    () => buildWeeks(startDate, endDate),
    [startDate, endDate]
  );

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  useEffect(() => {
    if (!open || !project) return;
    const globalCap = project.capacity_percent || 0;
    setGlobalCapacity(globalCap);
    setError("");

    const start = project.start_date ? new Date(project.start_date) : null;
    const end = project.end_date ? new Date(project.end_date) : null;
    setStartDate(start);
    setEndDate(end);

    // Freeze past weeks at their effective capacity so changing the global
    // slider doesn't retroactively alter already-elapsed weeks.
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const existingOverrides = project.weekly_capacity || {};
    const locked = { ...existingOverrides };
    buildWeeks(start, end).forEach((week) => {
      if (week.end < now && !(week.key in locked)) {
        locked[week.key] = globalCap;
      }
    });
    setWeeklyCapacities(locked);

    getEmployeeCapacity(employee.employee_id, project.project_id, project.start_date, project.end_date)
      .then((cap) => setAvailableCapacity(cap.available_capacity_percent))
      .catch(() => setAvailableCapacity(null));
  }, [open, project, employee]);

  const getWeekCap = (key) => weeklyCapacities[key] ?? globalCapacity;

  const setWeekCap = (key, value) =>
    setWeeklyCapacities((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!project || !employee) return;
    try {
      setSaving(true);
      setError("");
      const fullProject = await getProject(project.project_id);
      const mergedAssignedTo = fullProject.assigned_to.map((a) =>
        a.employee_id === employee.employee_id
          ? { ...a, capacity_percent: globalCapacity, weekly_capacity: weeklyCapacities }
          : a
      );
      await updateProject(project.project_id, {
        project_name: fullProject.project_name,
        description: fullProject.description,
        start_date: startDate ? localStr(startDate) : fullProject.start_date,
        end_date: endDate ? localStr(endDate) : fullProject.end_date,
        project_type: fullProject.project_type,
        client_name: fullProject.client_name,
        po_number: fullProject.po_number,
        project_manager_id: fullProject.project_manager_id,
        assigned_to: mergedAssignedTo,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      console.error("Error updating capacity:", err);
      setError(err?.response?.data?.detail || "Failed to update capacity");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Edit Capacity{project ? ` — ${project.project_name}` : ""}
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <AppDatePicker
            label="Start Date"
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            maxDate={endDate}
            width="50%"
          />
          <AppDatePicker
            label="End Date"
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            width="50%"
          />
        </Box>

        {/* Default capacity applied to all weeks without an override */}
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography fontWeight={600} fontSize={13}>Default Weekly Capacity</Typography>
            <Chip
              label={`${globalCapacity}%`}
              size="small"
              sx={{ background: capColor(globalCapacity), color: "#fff", fontWeight: 700 }}
            />
          </Box>
          <Slider
            value={globalCapacity}
            onChange={(_, v) => setGlobalCapacity(v)}
            min={0} max={100} step={5}
            marks={[0, 25, 50, 75, 100].map((v) => ({ value: v, label: `${v}%` }))}
            valueLabelDisplay="auto"
            sx={{ color: capColor(globalCapacity) }}
          />
          <Typography fontSize={11} color="text.secondary" mt={0.5}>
            Applied to weeks without a custom override
          </Typography>
        </Box>

        {availableCapacity !== null && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Available capacity (excluding this project): {availableCapacity}%
          </Alert>
        )}

        {weeks.length > 0 && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography fontSize={12} color="text.secondary">Week-by-week overrides</Typography>
            </Divider>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxHeight: 380, overflowY: "auto", pr: 0.5 }}>
              {weeks.map((week) => {
                const completed = week.end < today;
                const isCurrent = week.start <= today && today <= week.end;
                const cap = getWeekCap(week.key);
                const hasOverride = weeklyCapacities[week.key] !== undefined;

                return (
                  <Box
                    key={week.key}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: isCurrent ? "primary.main" : "divider",
                      bgcolor: completed ? "action.hover" : "background.paper",
                      opacity: completed ? 0.7 : 1,
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={completed ? 0 : 1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title={completed ? "Completed — read only" : "Editable"}>
                          {completed
                            ? <LockRoundedIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                            : <LockOpenRoundedIcon sx={{ fontSize: 14, color: isCurrent ? "primary.main" : "text.secondary" }} />
                          }
                        </Tooltip>
                        <Typography fontSize={12} fontWeight={600} color={completed ? "text.disabled" : "text.primary"}>
                          {fmtDate(week.start)} – {fmtDate(week.end)}
                        </Typography>
                        {isCurrent && (
                          <Chip label="This Week" size="small" color="primary" sx={{ height: 18, fontSize: 10 }} />
                        )}
                        {hasOverride && !completed && (
                          <Chip label="Custom" size="small" sx={{ height: 18, fontSize: 10, bgcolor: "#f0fdf4", color: "#16a34a" }} />
                        )}
                      </Box>
                      <Chip
                        label={`${cap}%`}
                        size="small"
                        sx={{
                          background: completed ? "#e2e8f0" : capColor(cap),
                          color: completed ? "#64748b" : "#fff",
                          fontWeight: 700
                        }}
                      />
                    </Box>

                    {!completed && (
                      <Slider
                        value={cap}
                        onChange={(_, v) => setWeekCap(week.key, v)}
                        min={0} max={100} step={5}
                        valueLabelDisplay="auto"
                        sx={{ color: capColor(cap), py: 0.5 }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <AppButton variant="outlined" onClick={onClose} disabled={saving}>Cancel</AppButton>
        <AppButton variant="contained" onClick={handleSave} loading={saving}>Save</AppButton>
      </DialogActions>
    </Dialog>
  );
}

export default EditCapacityDialog;
